/**
 * useApiCache Hook
 * 
 * Provides centralized API request caching and deduplication for React components.
 * Prevents duplicate API calls within a short time window and caches responses.
 * 
 * Features:
 * - Request deduplication: Multiple components calling the same endpoint share the same promise
 * - Memory cache with TTL: Cached responses are reused for 30 seconds
 * - Automatic cache invalidation: Cache expires after TTL
 * - Type-safe: Works with any API client function
 */

import { useRef, useCallback, useEffect } from 'react';

// Global cache storage (shared across all hook instances)
const requestCache = new Map();
const inFlightRequests = new Map();
const CACHE_TTL_MS = 30000; // 30 seconds
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes - data older than this is considered stale

/**
 * Generate a cache key from endpoint and params
 */
function getCacheKey(endpoint, params = {}) {
    const paramStr = JSON.stringify(params);
    return `${endpoint}::${paramStr}`;
}

/**
 * Check if cached data is still valid (within TTL)
 */
function isCacheValid(cachedItem) {
    if (!cachedItem) return false;
    const age = Date.now() - cachedItem.timestamp;
    return age < CACHE_TTL_MS;
}

/**
 * Check if cached data is stale but usable (beyond TTL but within stale threshold)
 */
function isStale(cachedItem) {
    if (!cachedItem) return false;
    const age = Date.now() - cachedItem.timestamp;
    return age >= CACHE_TTL_MS && age < STALE_THRESHOLD_MS;
}

/**
 * Custom hook for API request caching and deduplication
 * 
 * @param {Function} apiFunction - The API function to call (e.g., apiClient.get)
 * @param {string} endpoint - The API endpoint (for cache key)
 * @param {Object} params - Optional parameters (for cache key)
 * @param {Array} dependencies - React dependencies array (same as useEffect)
 * 
 * @returns {Object} { data, loading, error, refetch }
 */
export function useApiCache(apiFunction, endpoint, params = {}, dependencies = []) {
    const cacheKey = getCacheKey(endpoint, params);
    const dataRef = useRef(null);
    const loadingRef = useRef(false);
    const errorRef = useRef(null);

    const fetchData = useCallback(async (forceRefresh = false) => {
        // Check cache first
        const cached = requestCache.get(cacheKey);
        const hasValidCache = isCacheValid(cached);
        const hasStaleCache = isStale(cached);
        
        // Stale-while-revalidate: return stale data immediately if available
        if (!forceRefresh && hasStaleCache) {
            // Return stale data immediately (instant UI update)
            dataRef.current = cached.data;
            loadingRef.current = false;
            errorRef.current = null;
            
            // Refresh in background (don't await - fire and forget)
            if (!inFlightRequests.has(cacheKey)) {
                fetchData(true).catch(() => {
                    // Background refresh failed - keep stale data
                    // Error is silently ignored to maintain UX
                });
            }
            
            return cached.data;
        }
        
        // If we have valid cache and not forcing refresh, return it
        if (!forceRefresh && hasValidCache) {
            dataRef.current = cached.data;
            loadingRef.current = false;
            errorRef.current = null;
            return cached.data;
        }

        // Check if request is already in flight
        if (inFlightRequests.has(cacheKey)) {
            try {
                const data = await inFlightRequests.get(cacheKey);
                dataRef.current = data;
                loadingRef.current = false;
                errorRef.current = null;
                return data;
            } catch (err) {
                errorRef.current = err;
                loadingRef.current = false;
                throw err;
            }
        }

        // Start new request
        // Only show loading if we don't have stale data to show
        if (!hasStaleCache) {
            loadingRef.current = true;
        }
        errorRef.current = null;
        
        const requestPromise = (async () => {
            try {
                const response = await apiFunction(endpoint, params);
                
                // Store in cache
                requestCache.set(cacheKey, {
                    data: response,
                    timestamp: Date.now()
                });
                
                // Remove from in-flight
                inFlightRequests.delete(cacheKey);
                
                // Update data (this will trigger re-render if component is still mounted)
                dataRef.current = response;
                loadingRef.current = false;
                errorRef.current = null;
                
                return response;
            } catch (err) {
                // Remove from in-flight on error
                inFlightRequests.delete(cacheKey);
                errorRef.current = err;
                loadingRef.current = false;
                throw err;
            }
        })();
        
        // Store in-flight request
        inFlightRequests.set(cacheKey, requestPromise);
        
        return requestPromise;
    }, [apiFunction, cacheKey, ...dependencies]);

    const refetch = useCallback(() => {
        // Clear cache and refetch
        requestCache.delete(cacheKey);
        inFlightRequests.delete(cacheKey);
        return fetchData();
    }, [cacheKey, fetchData]);

    // Auto-fetch on mount and when dependencies change
    useEffect(() => {
        fetchData().catch(() => {
            // Error handled in fetchData
        });
    }, [fetchData]);

    return {
        data: dataRef.current,
        loading: loadingRef.current,
        error: errorRef.current,
        refetch
    };
}

/**
 * Clear all cached data (useful for logout or manual refresh)
 */
export function clearApiCache() {
    requestCache.clear();
    inFlightRequests.clear();
}

/**
 * Clear cache for a specific endpoint
 */
export function clearEndpointCache(endpoint, params = {}) {
    const cacheKey = getCacheKey(endpoint, params);
    requestCache.delete(cacheKey);
    inFlightRequests.delete(cacheKey);
}

